import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  margin-bottom: 20px;
  
  h3 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 18px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    color: #666;
    font-size: 14px;
  }
`;

const DayRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: none;
  }
`;

const DayLabel = styled.div`
  width: 120px;
  font-weight: 500;
  color: #333;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 26px;
  margin-right: 20px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
    
    &:checked + span {
      background-color: #007AFF;
    }
    
    &:checked + span:before {
      transform: translateX(24px);
    }
  }
  
  span {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: .3s;
    border-radius: 34px;
    
    &:before {
      position: absolute;
      content: "";
      height: 18px;
      width: 18px;
      left: 4px;
      bottom: 4px;
      background-color: white;
      transition: .3s;
      border-radius: 50%;
    }
  }
`;

const TimeSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: white;
  margin: 0 8px;
  
  &:disabled {
    background: #f5f5f5;
    color: #999;
  }
`;

const ApplyToAllButton = styled.button`
  background: none;
  border: none;
  color: #007AFF;
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  margin-left: auto;
  
  &:hover {
    background: rgba(0, 122, 255, 0.1);
  }
  
  &:disabled {
    color: #999;
    cursor: not-allowed;
  }
`;

const PresetButton = styled.button`
  background: ${props => props.selected ? '#007AFF' : 'white'};
  color: ${props => props.selected ? 'white' : '#333'};
  border: 1px solid ${props => props.selected ? '#007AFF' : '#ddd'};
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  margin-right: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.selected ? '#0056b3' : '#f5f5f5'};
  }
`;

const PresetSection = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
`;

const ShowingSchedule = ({ schedule, onChange }) => {
  // Define the correct day order (Monday through Sunday)
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  const timeOptions = Array.from({ length: 24 * 2 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = (i % 2) * 30;
    const time = new Date(2000, 0, 1, hour, minute);
    return {
      value: hour.toString().padStart(2, '0') + ':' + minute.toString().padStart(2, '0'),
      label: time.toLocaleTimeString('en-US', { 
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
  });

  const presets = {
    business: {
      name: 'Business Hours',
      schedule: {
        monday: { enabled: true, start: '09:00', end: '17:00' },
        tuesday: { enabled: true, start: '09:00', end: '17:00' },
        wednesday: { enabled: true, start: '09:00', end: '17:00' },
        thursday: { enabled: true, start: '09:00', end: '17:00' },
        friday: { enabled: true, start: '09:00', end: '17:00' },
        saturday: { enabled: false, start: '09:00', end: '17:00' },
        sunday: { enabled: false, start: '09:00', end: '17:00' }
      }
    },
    extended: {
      name: 'Extended Hours',
      schedule: {
        monday: { enabled: true, start: '08:00', end: '20:00' },
        tuesday: { enabled: true, start: '08:00', end: '20:00' },
        wednesday: { enabled: true, start: '08:00', end: '20:00' },
        thursday: { enabled: true, start: '08:00', end: '20:00' },
        friday: { enabled: true, start: '08:00', end: '20:00' },
        saturday: { enabled: true, start: '09:00', end: '17:00' },
        sunday: { enabled: true, start: '09:00', end: '17:00' }
      }
    },
    weekend: {
      name: 'Weekend Only',
      schedule: {
        monday: { enabled: false, start: '09:00', end: '17:00' },
        tuesday: { enabled: false, start: '09:00', end: '17:00' },
        wednesday: { enabled: false, start: '09:00', end: '17:00' },
        thursday: { enabled: false, start: '09:00', end: '17:00' },
        friday: { enabled: false, start: '09:00', end: '17:00' },
        saturday: { enabled: true, start: '09:00', end: '17:00' },
        sunday: { enabled: true, start: '09:00', end: '17:00' }
      }
    }
  };

  const handleDayToggle = (day) => {
    const newSchedule = { ...schedule };
    newSchedule[day] = {
      ...schedule[day],
      enabled: !schedule[day].enabled
    };
    onChange(newSchedule);
  };

  const handleTimeChange = (day, field, value) => {
    const newSchedule = { ...schedule };
    newSchedule[day] = {
      ...schedule[day],
      [field]: value
    };
    onChange(newSchedule);
  };

  const applyToAll = (day) => {
    const template = schedule[day];
    const newSchedule = {};
    Object.keys(schedule).forEach(key => {
      newSchedule[key] = { ...template };
    });
    onChange(newSchedule);
  };

  const applyPreset = (presetName) => {
    onChange(presets[presetName].schedule);
  };

  return (
    <Container>
      <Header>
        <h3>📅 Showing Schedule</h3>
        <p>Set your property's showing availability. You can customize hours for each day or use preset schedules.</p>
      </Header>

      <PresetSection>
        {Object.entries(presets).map(([key, preset]) => (
          <PresetButton
            key={key}
            selected={JSON.stringify(schedule) === JSON.stringify(preset.schedule)}
            onClick={() => applyPreset(key)}
          >
            {preset.name}
          </PresetButton>
        ))}
      </PresetSection>

      {dayOrder.map((day) => {
        const settings = schedule[day];
        if (!settings) return null; // Skip if day doesn't exist in schedule
        
        return (
          <DayRow key={day}>
            <DayLabel>{day.charAt(0).toUpperCase() + day.slice(1)}</DayLabel>
          
          <ToggleSwitch>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={() => handleDayToggle(day)}
            />
            <span />
          </ToggleSwitch>
          
          <TimeSelect
            value={settings.start}
            onChange={(e) => handleTimeChange(day, 'start', e.target.value)}
            disabled={!settings.enabled}
          >
            {timeOptions.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </TimeSelect>
          
          <span>to</span>
          
          <TimeSelect
            value={settings.end}
            onChange={(e) => handleTimeChange(day, 'end', e.target.value)}
            disabled={!settings.enabled}
          >
            {timeOptions.map(time => (
              <option key={time.value} value={time.value}>
                {time.label}
              </option>
            ))}
          </TimeSelect>
          
          <ApplyToAllButton
            onClick={() => applyToAll(day)}
            disabled={!settings.enabled}
          >
            Apply to all days
          </ApplyToAllButton>
        </DayRow>
        );
      })}
    </Container>
  );
};

export default ShowingSchedule;